import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import {
  HelpArticleSummaryDto,
  HelpManifestDto,
  HelpScreenDto,
  HelpService,
} from '@api-client';

interface HelpTopicGroup {
  name: string;
  articles: HelpArticleSummaryDto[];
}

interface HelpTopicTone {
  color: string;
  background: string;
}

/**
 * Context-aware in-app help (ZEY-534, hybrid anchors + tasks ZEY-1492). Opens a
 * right-side drawer and renders the markdown help article for the screen you're
 * on (matched on the top route segment). The screen's anchor article opens by
 * default with its task articles listed beneath; `?help=<slug>` deep-links
 * straight to one article (ZEY-1328) and auto-opens the drawer on load.
 * Falls back to an index of all articles when the current screen has none.
 */
@Component({
  selector: 'app-help-drawer',
  templateUrl: './help-drawer.component.html',
  styleUrls: ['./help-drawer.component.scss'],
  standalone: false,
})
export class HelpDrawerComponent implements OnInit {
  isOpen = false;
  loading = false;
  showIndex = false;
  articleFailed = false;
  title = 'Help';
  bodyHtml: SafeHtml | null = null;
  articles: HelpArticleSummaryDto[] = [];
  /** Index articles grouped by manifest module. */
  groups: HelpTopicGroup[] = [];
  /** Filters the index. Does not change which article is loaded. */
  search = '';
  /** Other articles on the same screen as the one being shown. */
  related: HelpArticleSummaryDto[] = [];
  current: HelpArticleSummaryDto | null = null;

  private static readonly topicTones: HelpTopicTone[] = [
    { color: '#4C7FE5', background: '#E8F0FE' },
    { color: '#E25B8A', background: '#FDE8F0' },
    { color: '#7B61C9', background: '#F1EBFC' },
    { color: '#2BA8A0', background: '#E5F7F6' },
    { color: '#2EAE6A', background: '#E6F7EE' },
    { color: '#E8943A', background: '#FFF1E4' },
    { color: '#5B6BD6', background: '#ECEEFB' },
    { color: '#D4546A', background: '#FDECEE' },
  ];

  private static readonly topicIcons: Array<[RegExp, string]> = [
    [/delete|remove|trash/, 'trash'],
    [/edit|deactivat/, 'edit'],
    [/reverse|undo/, 'refresh'],
    [/status|approve/, 'check-circle'],
    [/currency|foreign|rate|exchange/, 'exchange'],
    [/payment|pay|allocate/, 'card'],
    [/receipt|cheque/, 'cheque'],
    [/expense|landed|cost/, 'wallet'],
    [/employee|user|contact|customer/, 'user'],
    [/mail|email/, 'mail'],
    [/calendar|holiday|leave|time-off|attendance/, 'calendar'],
    [/bank|reconcil/, 'bank'],
    [/print/, 'print'],
    [/stock|warehouse|truck|transfer/, 'truck'],
    [/map|framework/, 'transfer'],
    [/chart|classification|account/, 'folder'],
  ];

  private manifest: HelpManifestDto | null = null;
  private manifestLang: string | null = null;
  private activeScreen: HelpScreenDto | null = null;
  private generation = 0;

  constructor(
    private help: HelpService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Deep link (ZEY-1328): /journals?help=post-a-manual-journal-entry
    this.route.queryParamMap.subscribe((params) => {
      const slug = params.get('help');
      if (!slug) {
        return;
      }
      this.beginOpen();
      void this.openBySlug(slug);
    });

    this.translate.onLangChange.subscribe(() => {
      const next = this.lang();
      if (this.manifestLang === next) {
        return;
      }
      this.manifest = null;
      this.manifestLang = null;
      if (!this.isOpen) {
        return;
      }
      void this.reloadForLanguage();
    });
  }

  open(): void {
    this.beginOpen();
    void this.openForCurrentRoute();
  }

  close(): void {
    this.isOpen = false;
  }

  openIndex(): void {
    this.generation++;
    this.loading = false;
    this.showIndex = true;
    this.articleFailed = false;
    this.title = 'Help';
    this.bodyHtml = null;
    this.current = null;
    this.related = [];
  }

  get visibleGroups(): HelpTopicGroup[] {
    const query = this.search.trim().toLowerCase();
    if (!query) {
      return this.groups;
    }
    return this.groups
      .map((group) => ({
        name: group.name,
        articles: group.articles.filter((article) =>
          `${article.title ?? ''} ${article.slug ?? ''}`.toLowerCase().includes(query),
        ),
      }))
      .filter((group) => group.articles.length > 0);
  }

  topicIcon(article: HelpArticleSummaryDto): string {
    const text = `${article.slug ?? ''} ${article.title ?? ''}`.toLowerCase();
    const match = HelpDrawerComponent.topicIcons.find(([pattern]) => pattern.test(text));
    return match?.[1] ?? 'document';
  }

  topicTone(article: HelpArticleSummaryDto): HelpTopicTone {
    const slug = article.slug ?? '';
    let hash = 0;
    for (let i = 0; i < slug.length; i++) {
      hash = (hash + slug.charCodeAt(i) * (i + 1)) % HelpDrawerComponent.topicTones.length;
    }
    return HelpDrawerComponent.topicTones[hash];
  }

  sectionIcon(name: string): string {
    const label = name.toLowerCase();
    if (label.includes('journal')) return 'file';
    if (label.includes('account')) return 'user';
    if (label.includes('payment')) return 'card';
    if (label.includes('receipt')) return 'cheque';
    if (label.includes('employee') || label.includes('hr')) return 'contact';
    if (label.includes('stock') || label.includes('inventor')) return 'truck';
    return 'folder';
  }

  get fullPageUrl(): string | null {
    const slug = this.current?.slug;
    return slug ? `/help/${encodeURIComponent(slug)}` : null;
  }

  async load(article: HelpArticleSummaryDto): Promise<void> {
    if (!article.slug) {
      return;
    }
    const generation = ++this.generation;
    this.showIndex = false;
    this.articleFailed = false;
    this.loading = true;
    this.title = article.title || 'Help';
    this.current = article;
    const screen = this.screenFor(article.slug);
    this.related = screen ? this.othersOn(screen, article.slug) : [];
    try {
      const dto = await firstValueFrom(
        this.help.helpArticlesSlugGet(article.slug, this.lang()),
      );
      if (generation !== this.generation) {
        return;
      }
      const slug = dto.slug || article.slug;
      this.title = dto.title || this.title;
      this.current = { ...article, slug, title: this.title };
      this.bodyHtml = this.sanitizer.bypassSecurityTrustHtml(dto.html ?? '');
      const owning = this.screenFor(slug);
      if (owning) {
        this.related = this.othersOn(owning, slug);
      } else if (dto.related?.length) {
        this.related = this.anchorsFirst(
          dto.related
            .filter((item) => !!item.slug && item.slug !== slug)
            .map((item) => ({ slug: item.slug, title: item.title, parent: item.parent })),
        );
      }
    } catch {
      if (generation !== this.generation) {
        return;
      }
      this.bodyHtml = null;
      this.articleFailed = true;
    } finally {
      if (generation === this.generation) {
        this.loading = false;
      }
    }
  }

  private beginOpen(): void {
    this.generation++;
    this.isOpen = true;
    this.loading = true;
    this.showIndex = false;
    this.articleFailed = false;
    this.title = 'Help';
    this.bodyHtml = null;
    this.current = null;
    this.related = [];
    this.search = '';
  }

  private async openForCurrentRoute(): Promise<void> {
    const generation = this.generation;
    const manifest = await this.ensureManifest();
    if (generation !== this.generation) {
      return;
    }
    const screen = this.matchScreen(manifest);
    const list = this.anchorsFirst(screen?.articles ?? []).filter((article) => !!article.slug);
    if (list.length && screen) {
      this.activeScreen = screen;
      await this.load(list[0]);
      return;
    }
    this.activeScreen = null;
    this.openIndex();
  }

  private async openBySlug(slug: string): Promise<void> {
    const generation = this.generation;
    const manifest = await this.ensureManifest();
    if (generation !== this.generation) {
      return;
    }
    this.activeScreen = this.matchScreen(manifest);
    const article = this.findBySlug(manifest, slug) ?? { slug, title: slug };
    await this.load(article);
  }

  private async reloadForLanguage(): Promise<void> {
    const generation = ++this.generation;
    const article = this.current;
    const index = this.showIndex;
    this.loading = true;
    this.articleFailed = false;
    await this.ensureManifest();
    if (generation !== this.generation) {
      return;
    }
    if (index || !article?.slug) {
      this.loading = false;
      this.showIndex = true;
      this.current = null;
      this.related = [];
      this.bodyHtml = null;
      this.title = 'Help';
      return;
    }
    await this.load(article);
  }

  private async ensureManifest(): Promise<HelpManifestDto> {
    const lang = this.lang();
    if (this.manifest && this.manifestLang === lang) {
      return this.manifest;
    }
    try {
      const manifest = await firstValueFrom(this.help.helpManifestGet(lang));
      if (this.lang() !== lang) {
        return manifest;
      }
      this.manifest = manifest;
      this.manifestLang = lang;
      const index = this.indexFrom(manifest);
      this.groups = index.groups;
      this.articles = index.articles;
      return manifest;
    } catch {
      if (this.lang() === lang) {
        this.manifest = null;
        this.manifestLang = null;
        this.articles = [];
        this.groups = [];
      }
      return { modules: [] };
    }
  }

  private lang(): string {
    const code = (this.translate.currentLang || this.translate.defaultLang || 'en').toLowerCase();
    return code.split('-')[0] || 'en';
  }

  private screens(manifest: HelpManifestDto): HelpScreenDto[] {
    return (manifest.modules ?? []).flatMap((module) => module.screens ?? []);
  }

  /** Longest manifest screen path that is the current route or a prefix of it. */
  private matchScreen(manifest: HelpManifestDto): HelpScreenDto | null {
    const path = this.currentPath();
    let best: HelpScreenDto | null = null;
    let bestLen = -1;
    for (const screen of this.screens(manifest)) {
      const screenPath = this.normalize(screen.path);
      if (!screenPath) {
        continue;
      }
      const matches = path === screenPath || path.startsWith(`${screenPath}/`);
      if (matches && screenPath.length > bestLen) {
        best = screen;
        bestLen = screenPath.length;
      }
    }
    return best;
  }

  private currentPath(): string {
    const url = this.router.url.split('?')[0].split('#')[0];
    return this.normalize(url);
  }

  private normalize(path: string | null | undefined): string {
    return (path ?? '').replace(/^\/+|\/+$/g, '');
  }

  private anchorsFirst<T extends { parent?: string }>(articles: T[]): T[] {
    return [...articles].sort((a, b) => Number(!!a.parent) - Number(!!b.parent));
  }

  private indexFrom(manifest: HelpManifestDto): { articles: HelpArticleSummaryDto[]; groups: HelpTopicGroup[] } {
    const seen = new Set<string>();
    const groups: HelpTopicGroup[] = [];
    for (const module of manifest.modules ?? []) {
      const articles: HelpArticleSummaryDto[] = [];
      for (const screen of module.screens ?? []) {
        for (const article of this.anchorsFirst(screen.articles ?? [])) {
          if (!article.slug || seen.has(article.slug)) {
            continue;
          }
          seen.add(article.slug);
          articles.push(article);
        }
      }
      if (articles.length) {
        groups.push({ name: module.name || 'Help', articles });
      }
    }
    return { articles: groups.flatMap((group) => group.articles), groups };
  }

  private findBySlug(manifest: HelpManifestDto, slug: string): HelpArticleSummaryDto | null {
    for (const screen of this.screens(manifest)) {
      const hit = (screen.articles ?? []).find((article) => article.slug === slug);
      if (hit) {
        return hit;
      }
    }
    return null;
  }

  /** Screen the article belongs to, preferring the one matched from the route. */
  private screenFor(slug: string): HelpScreenDto | null {
    if (!this.manifest) {
      return null;
    }
    const owning = this.screens(this.manifest).filter((screen) =>
      (screen.articles ?? []).some((article) => article.slug === slug),
    );
    if (this.activeScreen && owning.includes(this.activeScreen)) {
      return this.activeScreen;
    }
    return owning[0] ?? null;
  }

  private othersOn(screen: HelpScreenDto, slug: string): HelpArticleSummaryDto[] {
    return this.anchorsFirst(screen.articles ?? []).filter(
      (article) => !!article.slug && article.slug !== slug,
    );
  }
}
